/**
 * iter247 — Global Configuration Master
 *
 * 7 tabs: Categories · Subcategories · Items · Templates · PO Rules ·
 * Invoice Rules · Go-Live Rules — all collapsible via <details>.
 *
 * Independent of any franchise.  SA-only writes; audit trail lives at
 * `POST /api/config-master/audit/list`.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Ban, Search, History, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CLASSIFICATIONS = ['ASSET', 'INVENTORY', 'FEE', 'SERVICE', 'WORKING_CAPITAL'];
const BILLING_SOURCES = ['MFPL', 'FRANCHISEE_DIRECT'];

async function cmCall(path, body, token, method = 'POST') {
  const res = await fetch(`${API}/api/config-master${path}`, {
    method, headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...body }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.detail || 'Request failed');
  return j;
}

/* ── Categories tab ─────────────────────────────────────────── */
function CategoriesTab({ token, isSA, onOpenAudit }) {
  const [rows, setRows] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try { const r = await cmCall('/category/list', { include_inactive: showInactive }, token); setRows(r.items); }
    catch (e) { toast.error(e.message); }
  }, [showInactive, token]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.code.toLowerCase().includes(search.toLowerCase()));

  const save = async () => {
    if (!editing.code || !editing.name) return toast.error('Code + Name required');
    try { await cmCall('/category/save', editing, token); toast.success('Saved'); setEditing(null); load(); }
    catch (e) { toast.error(e.message); }
  };
  const deactivate = async (r) => {
    const reason = window.prompt(`Deactivate "${r.name}"? Reason:`);
    if (!reason) return;
    try { await cmCall(`/category/deactivate/${r.id}`, { reason }, token); toast.success('Deactivated'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1"><Search className="absolute left-2 top-2.5 w-3 h-3 text-muted-foreground" /><Input className="pl-7 h-8 text-xs" placeholder="Search category..." value={search} onChange={e => setSearch(e.target.value)} data-testid="cm-cat-search" /></div>
        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />Show inactive</label>
        <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-3 h-3" /></Button>
        {isSA && <Button size="sm" onClick={() => setEditing({ code: '', name: '', classification: 'ASSET', default_po_required: true, default_invoice_required: true, default_billing_source: 'MFPL', default_gst: 18, default_go_live_blocker: false, country_applicability: [], centre_type_applicability: [], sort_order: 100, active: true })} data-testid="btn-cm-cat-add"><Plus className="w-3 h-3 mr-1" />Add Category</Button>}
      </div>
      <table className="w-full text-xs" data-testid="cm-cat-table"><thead className="text-left border-b bg-muted/40"><tr><th className="p-1">Code</th><th>Name</th><th>Class</th><th className="text-center">PO</th><th className="text-center">Inv</th><th className="text-center">GST</th><th>Billing</th><th>Subs</th><th>Items</th><th>Status</th><th></th></tr></thead><tbody>
        {filtered.map(r => (
          <tr key={r.id} className={`border-b ${!r.active ? 'bg-muted/30' : ''}`} data-testid={`cm-cat-row-${r.code}`}>
            <td className="p-1 font-mono">{r.code}</td><td className="font-semibold">{r.name}</td>
            <td>{r.classification}</td>
            <td className="text-center">{r.default_po_required ? '✓' : '—'}</td>
            <td className="text-center">{r.default_invoice_required ? '✓' : '—'}</td>
            <td className="text-center">{r.default_gst}%</td><td>{r.default_billing_source}</td>
            <td className="text-center">{r.subcategory_count}</td>
            <td className="text-center">{r.item_count}</td>
            <td>{r.active ? <Badge variant="outline" className="text-green-700 border-green-500 text-[9px]">ACTIVE</Badge> : <Badge variant="outline" className="text-red-700 border-red-500 text-[9px]">INACTIVE</Badge>}</td>
            <td className="text-right space-x-1">
              <Button size="sm" variant="ghost" onClick={() => onOpenAudit('category', r.id)} data-testid={`btn-cm-cat-audit-${r.code}`}><History className="w-3 h-3" /></Button>
              {isSA && <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })} data-testid={`btn-cm-cat-edit-${r.code}`}><Edit2 className="w-3 h-3" /></Button>}
              {isSA && r.active && <Button size="sm" variant="ghost" onClick={() => deactivate(r)} data-testid={`btn-cm-cat-deact-${r.code}`}><Ban className="w-3 h-3 text-red-500" /></Button>}
            </td>
          </tr>
        ))}
        {filtered.length === 0 && <tr><td colSpan={11} className="text-center p-4 text-muted-foreground">No categories</td></tr>}
      </tbody></table>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="cm-cat-form-modal">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'Add'} Category</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><Label>Code *</Label><Input value={editing.code} onChange={e => setEditing(f => ({ ...f, code: e.target.value.toUpperCase() }))} data-testid="cm-cat-form-code" /></div>
              <div><Label>Name *</Label><Input value={editing.name} onChange={e => setEditing(f => ({ ...f, name: e.target.value }))} data-testid="cm-cat-form-name" /></div>
              <div><Label>Classification</Label>
                <Select value={editing.classification} onValueChange={v => setEditing(f => ({ ...f, classification: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CLASSIFICATIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Default Billing Source</Label>
                <Select value={editing.default_billing_source} onValueChange={v => setEditing(f => ({ ...f, default_billing_source: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{BILLING_SOURCES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Default GST %</Label><Input type="number" value={editing.default_gst} onChange={e => setEditing(f => ({ ...f, default_gst: +e.target.value }))} /></div>
              <div><Label>Sort Order</Label><Input type="number" value={editing.sort_order} onChange={e => setEditing(f => ({ ...f, sort_order: +e.target.value }))} /></div>
              <label className="flex items-center gap-2 col-span-1"><input type="checkbox" checked={editing.default_po_required} onChange={e => setEditing(f => ({ ...f, default_po_required: e.target.checked }))} />PO Required by default</label>
              <label className="flex items-center gap-2 col-span-1"><input type="checkbox" checked={editing.default_invoice_required} onChange={e => setEditing(f => ({ ...f, default_invoice_required: e.target.checked }))} />Invoice Required by default</label>
              <label className="flex items-center gap-2 col-span-1"><input type="checkbox" checked={editing.default_go_live_blocker} onChange={e => setEditing(f => ({ ...f, default_go_live_blocker: e.target.checked }))} />Go-Live Blocker</label>
              <label className="flex items-center gap-2 col-span-1"><input type="checkbox" checked={editing.active} onChange={e => setEditing(f => ({ ...f, active: e.target.checked }))} />Active</label>
              <div className="col-span-2"><Label>Country Applicability (comma-sep IN,AU,NZ,AE — blank = all)</Label><Input value={(editing.country_applicability || []).join(',')} onChange={e => setEditing(f => ({ ...f, country_applicability: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) }))} /></div>
              <div className="col-span-2"><Label>Centre-Type Applicability (comma-sep DINE_IN,DELIVERY,KIOSK — blank = all)</Label><Input value={(editing.centre_type_applicability || []).join(',')} onChange={e => setEditing(f => ({ ...f, centre_type_applicability: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) }))} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} data-testid="btn-cm-cat-form-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Subcategories tab ──────────────────────────────────────── */
function SubcategoriesTab({ token, isSA, cats, onOpenAudit }) {
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState('');
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try { const r = await cmCall('/subcategory/list', { category_id: cat || undefined }, token); setRows(r.items); }
    catch (e) { toast.error(e.message); }
  }, [cat, token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.code || !editing.name || !editing.category_id) return toast.error('Category + Code + Name required');
    try { await cmCall('/subcategory/save', editing, token); toast.success('Saved'); setEditing(null); load(); }
    catch (e) { toast.error(e.message); }
  };
  const deact = async (r) => {
    const reason = window.prompt(`Deactivate "${r.name}"? Reason:`); if (!reason) return;
    try { await cmCall(`/subcategory/deactivate/${r.id}`, { reason }, token); toast.success('Deactivated'); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Select value={cat || 'ALL'} onValueChange={v => setCat(v === 'ALL' ? '' : v)}>
          <SelectTrigger className="w-64"><SelectValue placeholder="Filter by Category" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">-- All categories --</SelectItem>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        {isSA && <Button size="sm" onClick={() => setEditing({ code: '', name: '', category_id: cat || (cats[0]?.id || ''), sort_order: 100, active: true })} data-testid="btn-cm-sub-add"><Plus className="w-3 h-3 mr-1" />Add Subcategory</Button>}
      </div>
      <table className="w-full text-xs" data-testid="cm-sub-table"><thead className="border-b bg-muted/40 text-left"><tr><th className="p-1">Code</th><th>Name</th><th>Category</th><th className="text-center">Items</th><th>Status</th><th></th></tr></thead><tbody>
        {rows.map(r => (
          <tr key={r.id} className={`border-b ${!r.active ? 'bg-muted/30' : ''}`} data-testid={`cm-sub-row-${r.code}`}>
            <td className="p-1 font-mono">{r.code}</td><td>{r.name}</td>
            <td>{cats.find(c => c.id === r.category_id)?.name || '—'}</td>
            <td className="text-center">{r.item_count}</td>
            <td>{r.active ? '✓' : '—'}</td>
            <td className="text-right space-x-1">
              <Button size="sm" variant="ghost" onClick={() => onOpenAudit('subcategory', r.id)}><History className="w-3 h-3" /></Button>
              {isSA && <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })}><Edit2 className="w-3 h-3" /></Button>}
              {isSA && r.active && <Button size="sm" variant="ghost" onClick={() => deact(r)}><Ban className="w-3 h-3 text-red-500" /></Button>}
            </td>
          </tr>
        ))}
      </tbody></table>
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent><DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'Add'} Subcategory</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><Label>Category *</Label>
                <Select value={editing.category_id} onValueChange={v => setEditing(f => ({ ...f, category_id: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Code *</Label><Input value={editing.code} onChange={e => setEditing(f => ({ ...f, code: e.target.value.toUpperCase() }))} /></div>
              <div className="col-span-2"><Label>Name *</Label><Input value={editing.name} onChange={e => setEditing(f => ({ ...f, name: e.target.value }))} /></div>
              <div><Label>Sort Order</Label><Input type="number" value={editing.sort_order} onChange={e => setEditing(f => ({ ...f, sort_order: +e.target.value }))} /></div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active} onChange={e => setEditing(f => ({ ...f, active: e.target.checked }))} />Active</label>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Items tab ──────────────────────────────────────────────── */
function ItemsTab({ token, isSA, cats, subs, onOpenAudit }) {
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState(''); const [sub, setSub] = useState(''); const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await cmCall('/item/list', { category_id: cat || undefined, subcategory_id: sub || undefined, search: search || undefined }, token);
      setRows(r.items);
    } catch (e) { toast.error(e.message); }
  }, [cat, sub, search, token]);
  useEffect(() => { load(); }, [load]);
  const subOpts = useMemo(() => subs.filter(s => !cat || s.category_id === cat), [subs, cat]);
  const save = async () => {
    if (!editing.code || !editing.description || !editing.category_id) return toast.error('Code + Description + Category required');
    try { await cmCall('/item/save', editing, token); toast.success('Saved'); setEditing(null); load(); }
    catch (e) { toast.error(e.message); }
  };
  const deact = async (r) => {
    const reason = window.prompt(`Deactivate "${r.description}"? Reason:`); if (!reason) return;
    try { await cmCall(`/item/deactivate/${r.id}`, { reason }, token); toast.success('Deactivated'); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={cat || 'ALL'} onValueChange={v => { setCat(v === 'ALL' ? '' : v); setSub(''); }}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">-- All --</SelectItem>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sub || 'ALL'} onValueChange={v => setSub(v === 'ALL' ? '' : v)} disabled={!cat}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Subcategory" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">-- All --</SelectItem>{subOpts.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative flex-1"><Search className="absolute left-2 top-2.5 w-3 h-3 text-muted-foreground" /><Input className="pl-7 h-8 text-xs" placeholder="Search description..." value={search} onChange={e => setSearch(e.target.value)} data-testid="cm-item-search" /></div>
        {isSA && <Button size="sm" onClick={() => setEditing({ code: '', description: '', category_id: cat || cats[0]?.id, subcategory_id: sub || null, unit: 'NO', default_qty: 1, default_rate: null, default_gst: 18, default_supplier: '', default_billing_source: 'MFPL', po_required: null, invoice_required: null, go_live_blocker: false, brand: '', size: '', hsn_code: '', link: '', remarks: '', rate_status: 'RATE_REQUIRED', sort_order: 100, active: true })} data-testid="btn-cm-item-add"><Plus className="w-3 h-3 mr-1" />Add Item</Button>}
      </div>
      <div className="text-xs text-muted-foreground">{rows.length} items</div>
      <div className="max-h-[60vh] overflow-y-auto border rounded">
        <table className="w-full text-xs" data-testid="cm-item-table"><thead className="border-b bg-muted/40 text-left sticky top-0"><tr><th className="p-1">Code</th><th>Description</th><th>Cat</th><th>Sub</th><th>Unit</th><th>Qty</th><th>Rate</th><th>GST</th><th>Rate Status</th><th></th></tr></thead><tbody>
          {rows.map(r => (
            <tr key={r.id} className={`border-b ${!r.active ? 'bg-muted/30' : ''}`} data-testid={`cm-item-row-${r.code}`}>
              <td className="p-1 font-mono">{r.code}</td><td>{r.description}</td>
              <td className="text-[10px]">{cats.find(c => c.id === r.category_id)?.name || '—'}</td>
              <td className="text-[10px]">{subs.find(s => s.id === r.subcategory_id)?.name || '—'}</td>
              <td>{r.unit}</td><td>{r.default_qty}</td>
              <td>{r.default_rate == null ? '—' : `₹${r.default_rate}`}</td>
              <td>{r.default_gst ?? '—'}%</td>
              <td><Badge variant="outline" className={`text-[9px] ${r.rate_status === 'RATE_REQUIRED' ? 'text-amber-700 border-amber-500' : 'text-green-700 border-green-500'}`}>{r.rate_status}</Badge></td>
              <td className="text-right space-x-1 whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => onOpenAudit('item', r.id)}><History className="w-3 h-3" /></Button>
                {isSA && <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })}><Edit2 className="w-3 h-3" /></Button>}
                {isSA && r.active && <Button size="sm" variant="ghost" onClick={() => deact(r)}><Ban className="w-3 h-3 text-red-500" /></Button>}
              </td>
            </tr>
          ))}
        </tbody></table>
      </div>
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cm-item-form-modal">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'Add'} Item</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><Label>Code *</Label><Input value={editing.code} onChange={e => setEditing(f => ({ ...f, code: e.target.value.toUpperCase() }))} /></div>
              <div className="col-span-2"><Label>Description *</Label><Input value={editing.description} onChange={e => setEditing(f => ({ ...f, description: e.target.value }))} /></div>
              <div><Label>Category *</Label>
                <Select value={editing.category_id} onValueChange={v => setEditing(f => ({ ...f, category_id: v, subcategory_id: null }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Subcategory</Label>
                <Select value={editing.subcategory_id || 'NONE'} onValueChange={v => setEditing(f => ({ ...f, subcategory_id: v === 'NONE' ? null : v }))}><SelectTrigger><SelectValue placeholder="(none)" /></SelectTrigger><SelectContent><SelectItem value="NONE">--</SelectItem>{subs.filter(s => s.category_id === editing.category_id).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Unit</Label><Input value={editing.unit} onChange={e => setEditing(f => ({ ...f, unit: e.target.value }))} /></div>
              <div><Label>Default Qty</Label><Input type="number" value={editing.default_qty} onChange={e => setEditing(f => ({ ...f, default_qty: +e.target.value }))} /></div>
              <div><Label>Default Rate</Label><Input type="number" value={editing.default_rate ?? ''} onChange={e => setEditing(f => ({ ...f, default_rate: e.target.value === '' ? null : +e.target.value, rate_status: e.target.value ? 'VALUED' : 'RATE_REQUIRED' }))} /></div>
              <div><Label>Default GST %</Label><Input type="number" value={editing.default_gst ?? ''} onChange={e => setEditing(f => ({ ...f, default_gst: e.target.value === '' ? null : +e.target.value }))} /></div>
              <div><Label>Default Supplier</Label><Input value={editing.default_supplier} onChange={e => setEditing(f => ({ ...f, default_supplier: e.target.value }))} /></div>
              <div><Label>Billing Source</Label><Select value={editing.default_billing_source} onValueChange={v => setEditing(f => ({ ...f, default_billing_source: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{BILLING_SOURCES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Brand</Label><Input value={editing.brand} onChange={e => setEditing(f => ({ ...f, brand: e.target.value }))} /></div>
              <div><Label>Size</Label><Input value={editing.size} onChange={e => setEditing(f => ({ ...f, size: e.target.value }))} /></div>
              <div><Label>HSN</Label><Input value={editing.hsn_code} onChange={e => setEditing(f => ({ ...f, hsn_code: e.target.value }))} /></div>
              <div className="col-span-3"><Label>Link / Reference</Label><Input value={editing.link} onChange={e => setEditing(f => ({ ...f, link: e.target.value }))} /></div>
              <div className="col-span-3"><Label>Remarks</Label><Textarea rows={2} value={editing.remarks} onChange={e => setEditing(f => ({ ...f, remarks: e.target.value }))} /></div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.go_live_blocker} onChange={e => setEditing(f => ({ ...f, go_live_blocker: e.target.checked }))} />Go-Live Blocker</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.po_required ?? false} onChange={e => setEditing(f => ({ ...f, po_required: e.target.checked }))} />PO Required (override cat)</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.invoice_required ?? false} onChange={e => setEditing(f => ({ ...f, invoice_required: e.target.checked }))} />Invoice Required (override cat)</label>
              <label className="flex items-center gap-2 col-span-3"><input type="checkbox" checked={editing.active} onChange={e => setEditing(f => ({ ...f, active: e.target.checked }))} />Active</label>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} data-testid="btn-cm-item-form-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── PO Category & Invoice Category tabs (iter248) ─────────────
   Standalone SA-managed lists.  These classify FINAL documents,
   NOT the item Categories/Subcategories above. */
function SimpleCatTab({ token, isSA, endpoint, label, testidPrefix }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await cmCall(`/${endpoint}/list`, { include_inactive: showInactive }, token);
      setRows(r.items);
    } catch (e) { toast.error(e.message); }
  }, [endpoint, showInactive, token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name?.trim()) return toast.error('Name required');
    try {
      await cmCall(`/${endpoint}/save`, editing, token);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e.message); }
  };
  const deact = async (r) => {
    const reason = window.prompt(`Deactivate "${r.name}"? Reason:`);
    if (!reason) return;
    try { await cmCall(`/${endpoint}/deactivate/${r.id}`, { reason }, token); toast.success('Deactivated'); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-xs text-muted-foreground flex-1">
          {label}s — reusable master list. Each franchise selects the applicable ones. Adding a new {label} here does NOT auto-apply it to existing franchises.
        </div>
        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />Show inactive</label>
        <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-3 h-3" /></Button>
        {isSA && <Button size="sm" onClick={() => setEditing({ code: '', name: '', description: '', sort_order: (rows.length + 1) * 10, active: true })} data-testid={`btn-${testidPrefix}-add`}><Plus className="w-3 h-3 mr-1" />Add {label}</Button>}
      </div>
      <table className="w-full text-xs" data-testid={`${testidPrefix}-table`}>
        <thead className="text-left border-b bg-muted/40">
          <tr><th className="p-1">Code</th><th>Name</th><th>Description</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className={`border-b ${!r.active ? 'bg-muted/30' : ''}`} data-testid={`${testidPrefix}-row-${r.code}`}>
              <td className="p-1 font-mono">{r.code}</td>
              <td className="font-semibold">{r.name}</td>
              <td className="text-muted-foreground">{r.description || '—'}</td>
              <td>{r.active ? <Badge variant="outline" className="text-green-700 border-green-500 text-[9px]">ACTIVE</Badge> : <Badge variant="outline" className="text-red-700 border-red-500 text-[9px]">INACTIVE</Badge>}</td>
              <td className="text-right space-x-1">
                {isSA && <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })} data-testid={`btn-${testidPrefix}-edit-${r.code}`}><Edit2 className="w-3 h-3" /></Button>}
                {isSA && r.active && <Button size="sm" variant="ghost" onClick={() => deact(r)} data-testid={`btn-${testidPrefix}-deact-${r.code}`}><Ban className="w-3 h-3 text-red-500" /></Button>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="text-center p-4 text-muted-foreground">No {label.toLowerCase()}s</td></tr>}
        </tbody>
      </table>
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid={`${testidPrefix}-form-modal`}>
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'Add'} {label}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><Label>Name *</Label><Input value={editing.name || ''} onChange={e => setEditing(f => ({ ...f, name: e.target.value }))} data-testid={`${testidPrefix}-form-name`} /></div>
              <div><Label>Code (auto)</Label><Input value={editing.code || ''} onChange={e => setEditing(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="Auto-generated" /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={editing.description || ''} onChange={e => setEditing(f => ({ ...f, description: e.target.value }))} /></div>
              <div><Label>Sort Order</Label><Input type="number" value={editing.sort_order ?? 100} onChange={e => setEditing(f => ({ ...f, sort_order: +e.target.value }))} /></div>
              <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={editing.active !== false} onChange={e => setEditing(f => ({ ...f, active: e.target.checked }))} />Active</label>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} data-testid={`btn-${testidPrefix}-form-save`}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Rules views (Templates / PO / Invoice / Go-Live) share a single derived view ── */
function RulesView({ cats, filter, title, empty }) {
  const rows = cats.filter(filter);
  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">{title}</div>
      {rows.length === 0 ? <div className="text-xs text-muted-foreground py-4 text-center">{empty}</div> : (
        <table className="w-full text-xs"><thead className="border-b bg-muted/40 text-left"><tr><th className="p-1">Code</th><th>Name</th><th>Classification</th><th>Billing</th><th>Details</th></tr></thead><tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b"><td className="p-1 font-mono">{r.code}</td><td>{r.name}</td><td>{r.classification}</td><td>{r.default_billing_source}</td>
              <td className="text-[10px]">
                PO: {r.default_po_required ? '✓' : '—'} · Inv: {r.default_invoice_required ? '✓' : '—'} · GLB: {r.default_go_live_blocker ? '✓' : '—'} · GST: {r.default_gst}%
              </td></tr>
          ))}
        </tbody></table>
      )}
    </div>
  );
}

/* ── Audit modal ────────────────────────────────────────────── */
function AuditModal({ open, onClose, token, entity, entityId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!open) return;
    cmCall('/audit/list', { entity, entity_id: entityId }, token).then(r => setRows(r.items)).catch(() => setRows([]));
  }, [open, entity, entityId, token]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="cm-audit-modal">
        <DialogHeader><DialogTitle>Audit History — {entity}/{entityId?.slice(0, 8)}</DialogTitle></DialogHeader>
        {rows.length === 0 ? <div className="text-xs text-muted-foreground py-4">No history.</div> : (
          <table className="w-full text-xs"><thead className="border-b bg-muted/40 text-left"><tr><th className="p-1">At</th><th>Actor</th><th>Action</th><th>Reason</th></tr></thead><tbody>
            {rows.map(r => (<tr key={r.id} className="border-b"><td className="p-1">{(r.at || '').slice(0, 19).replace('T', ' ')}</td><td>{r.actor}</td><td><Badge variant="outline" className="text-[9px]">{r.action}</Badge></td><td className="text-[10px]">{r.reason || '—'}</td></tr>))}
          </tbody></table>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Main page ──────────────────────────────────────────────── */
export default function ConfigurationMasterPage() {
  const { session } = useAuth();
  const token = session?.token;
  const isSA = !!session?.is_super_admin;
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [audit, setAudit] = useState({ open: false, entity: '', entityId: '' });

  const loadIndex = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        cmCall('/category/list', { include_inactive: true }, token),
        cmCall('/subcategory/list', {}, token),
      ]);
      setCats(c.items); setSubs(s.items);
    } catch (e) { toast.error(e.message); }
  }, [token]);
  useEffect(() => { if (token) loadIndex(); }, [loadIndex, token]);
  const openAudit = (entity, id) => setAudit({ open: true, entity, entityId: id });

  return (
    <div className="space-y-4 p-4" data-testid="config-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Configuration Master</h2>
          <div className="text-xs text-muted-foreground">Global reusable library — independent of any franchise. Applied as a snapshot into each franchise.</div>
        </div>
        <Badge className="bg-slate-700 text-white">{cats.length} Categories · {subs.length} Subcategories</Badge>
      </div>

      <Tabs defaultValue="po-cats">
        <TabsList className="grid grid-cols-9 w-full">
          <TabsTrigger value="po-cats" data-testid="cm-tab-po-cats">PO Categories</TabsTrigger>
          <TabsTrigger value="inv-cats" data-testid="cm-tab-inv-cats">Invoice Categories</TabsTrigger>
          <TabsTrigger value="categories" data-testid="cm-tab-categories">Item Categories</TabsTrigger>
          <TabsTrigger value="subcategories" data-testid="cm-tab-subcategories">Subcategories</TabsTrigger>
          <TabsTrigger value="items" data-testid="cm-tab-items">Items</TabsTrigger>
          <TabsTrigger value="templates" data-testid="cm-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="po-rules" data-testid="cm-tab-po-rules">PO Rules</TabsTrigger>
          <TabsTrigger value="inv-rules" data-testid="cm-tab-inv-rules">Invoice Rules</TabsTrigger>
          <TabsTrigger value="gl-rules" data-testid="cm-tab-gl-rules">Go-Live Rules</TabsTrigger>
        </TabsList>
        <TabsContent value="po-cats"><Card><CardContent className="pt-4"><SimpleCatTab token={token} isSA={isSA} endpoint="po-category" label="PO Category" testidPrefix="po-cat" /></CardContent></Card></TabsContent>
        <TabsContent value="inv-cats"><Card><CardContent className="pt-4"><SimpleCatTab token={token} isSA={isSA} endpoint="invoice-category" label="Invoice Category" testidPrefix="inv-cat" /></CardContent></Card></TabsContent>
        <TabsContent value="categories"><Card><CardContent className="pt-4"><CategoriesTab token={token} isSA={isSA} onOpenAudit={openAudit} /></CardContent></Card></TabsContent>
        <TabsContent value="subcategories"><Card><CardContent className="pt-4"><SubcategoriesTab token={token} isSA={isSA} cats={cats} onOpenAudit={openAudit} /></CardContent></Card></TabsContent>
        <TabsContent value="items"><Card><CardContent className="pt-4"><ItemsTab token={token} isSA={isSA} cats={cats} subs={subs} onOpenAudit={openAudit} /></CardContent></Card></TabsContent>
        <TabsContent value="templates"><Card><CardContent className="pt-4 text-xs text-muted-foreground">Templates group categories into ready-to-apply franchise starter kits. Coming next iteration — currently applying the full active master via <b>Apply Master</b> on any onboarding.</CardContent></Card></TabsContent>
        <TabsContent value="po-rules"><Card><CardContent className="pt-4"><RulesView cats={cats} filter={c => c.default_po_required && c.active} title="Categories that trigger a Purchase Order requirement by default." empty="No PO-required categories configured." /></CardContent></Card></TabsContent>
        <TabsContent value="inv-rules"><Card><CardContent className="pt-4"><RulesView cats={cats} filter={c => c.default_invoice_required && c.active} title="Categories that trigger an MFPL Invoice requirement by default." empty="No invoice-required categories configured." /></CardContent></Card></TabsContent>
        <TabsContent value="gl-rules"><Card><CardContent className="pt-4"><RulesView cats={cats} filter={c => c.default_go_live_blocker && c.active} title="Categories that BLOCK Go-Live until satisfied." empty="No Go-Live blocker categories configured." /></CardContent></Card></TabsContent>
      </Tabs>

      <AuditModal open={audit.open} onClose={() => setAudit({ open: false, entity: '', entityId: '' })} token={token} entity={audit.entity} entityId={audit.entityId} />
    </div>
  );
}
